import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import {
  CONTACT_METHOD_OPTIONS,
  FOLLOWUP_CATEGORY_OPTIONS,
} from '@/features/activity/hooks/useFollowups';

// Item 44 — "all add follow-up forms should be the same".
//
// This is the capture set the Follow-up panel (TimelinePanel) has always used,
// extracted so the forms that create a follow-up alongside a record — quotation,
// pipeline create/edit, the disposition dialog, the dashboard quick action —
// render the identical fields instead of each inventing a narrower one. The
// server writers now persist these keys too (src/shared/followups/capture-fields.js),
// so nothing typed here is silently discarded.

export interface FollowupCapture {
  followupNote: string;
  followupCategory: string;
  customCategory: string;
  contactMethod: string;
  customerResponse: string;
  requirement: string;
  nextAction: string;
  details: string;
  followupDate: string;
}

export const EMPTY_FOLLOWUP_CAPTURE: FollowupCapture = {
  followupNote: '',
  followupCategory: '',
  customCategory: '',
  contactMethod: '',
  customerResponse: '',
  requirement: '',
  nextAction: '',
  details: '',
  followupDate: '',
};

interface Props {
  value: FollowupCapture;
  onChange: (next: FollowupCapture) => void;
  /** Whether the note is the form's own mandatory field (quotation) or optional. */
  noteRequired?: boolean;
  /** Pipeline forms schedule a day; the panel and quick action keep a time. */
  dateType?: 'date' | 'datetime-local';
  dateLabel?: string;
}

export function FollowupCaptureFields({
  value,
  onChange,
  noteRequired = false,
  dateType = 'date',
  dateLabel = 'Next followup on',
}: Props) {
  const set = (patch: Partial<FollowupCapture>) => onChange({ ...value, ...patch });

  // Styling only — the field set, names and order are the shared contract every
  // follow-up form must match (item 44). Nothing here changes what is captured.
  return (
    <div className="flex flex-col gap-4 rounded-card-sm border border-border bg-muted/40 p-3.5">
      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label="Follow-up Category"
          name="followup-category"
          placeholder="Select category"
          value={value.followupCategory}
          onChange={(e) => set({ followupCategory: e.target.value })}
          options={FOLLOWUP_CATEGORY_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
        />
        {value.followupCategory === 'custom' && (
          <Input
            label="Custom Category"
            placeholder="Type custom category name (free to type)"
            value={value.customCategory}
            onChange={(e) => set({ customCategory: e.target.value })}
            maxLength={100}
          />
        )}
      </div>

      <Input
        label={noteRequired ? 'Follow-up Note *' : 'Note'}
        placeholder="What happened, and what's next?"
        value={value.followupNote}
        onChange={(e) => set({ followupNote: e.target.value })}
        maxLength={300}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label="Contact method"
          name="followup-contact-method"
          placeholder="Not recorded"
          value={value.contactMethod}
          onChange={(e) => set({ contactMethod: e.target.value })}
          options={CONTACT_METHOD_OPTIONS.filter((o) => o.value).map((o) => ({ label: o.label, value: o.value }))}
        />
        <Input
          label={noteRequired ? `${dateLabel} *` : dateLabel}
          type={dateType}
          value={value.followupDate}
          onChange={(e) => set({ followupDate: e.target.value })}
        />
        <Input
          label="Customer response (optional)"
          placeholder="What did they say?"
          value={value.customerResponse}
          onChange={(e) => set({ customerResponse: e.target.value })}
          maxLength={2000}
        />
        <Input
          label="Requirement (optional)"
          placeholder="What do they need?"
          value={value.requirement}
          onChange={(e) => set({ requirement: e.target.value })}
          maxLength={2000}
        />
        <Input
          label="Next action (optional)"
          placeholder="What happens next?"
          value={value.nextAction}
          onChange={(e) => set({ nextAction: e.target.value })}
          maxLength={2000}
        />
      </div>

      <Input
        label="Details (optional)"
        placeholder="Anything worth keeping on the record"
        value={value.details}
        onChange={(e) => set({ details: e.target.value })}
        maxLength={5000}
      />
    </div>
  );
}

/**
 * The payload keys the writers read. `category` is sent as well as
 * `followupCategory` because the quotation route normalises through
 * document-rules.js, which has always accepted either spelling.
 */
export function followupCapturePayload(value: FollowupCapture) {
  const clean = (v: string) => String(v ?? '').trim();
  return {
    followupNote: clean(value.followupNote),
    note: clean(value.followupNote),
    details: clean(value.details),
    contactMethod: clean(value.contactMethod),
    customerResponse: clean(value.customerResponse),
    requirement: clean(value.requirement),
    nextAction: clean(value.nextAction),
    followupCategory: clean(value.followupCategory),
    category: clean(value.followupCategory),
    customCategory: value.followupCategory === 'custom' ? clean(value.customCategory) : '',
    followupDate: clean(value.followupDate),
    date: clean(value.followupDate),
  };
}
